import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CheckCircle2, Award, Users, HardHat } from 'lucide-react';
const Counter = ({
  end,
  duration = 2,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  useEffect(() => {
    if (inView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [inView, end, duration]);
  return <span ref={ref} data-id="element-12">
      {count}
      {suffix}
    </span>;
};
export function About() {
  const controls = useAnimation();
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);
  const stats = [{
    icon: <Award size={24} data-id="element-13" />,
    value: 20,
    suffix: '+',
    label: 'Years of Experience'
  }, {
    icon: <HardHat size={24} data-id="element-14" />,
    value: 500,
    suffix: '+',
    label: 'Projects Completed'
  }, {
    icon: <Users size={24} data-id="element-15" />,
    value: 100,
    suffix: '%',
    label: 'Satisfied Clients'
  }];
  const features = ['Comprehensive construction permit management', 'Rigorous supervision at every stage', 'Time and resource optimization', 'Personalized attention and direct communication'];
  return <section id="about" className="py-24 bg-light" data-id="element-16">
      <div className="container mx-auto px-6 md:px-12" data-id="element-17">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-18">
          {/* Image Side */}
          <motion.div ref={ref} initial="hidden" animate={controls} variants={{
          hidden: {
            opacity: 0,
            x: -50
          },
          visible: {
            opacity: 1,
            x: 0,
            transition: {
              duration: 0.8
            }
          }
        }} className="relative" data-id="element-19">
            <div className="relative z-10 rounded-sm overflow-hidden shadow-2xl" data-id="element-20">
              <img src="https://cdn.magicpatterns.com/uploads/mS36xAiYZkNkmVeUbb8Znf/Cover_de_planos_en_cuaderno.jpg" alt="Architectural blueprints and design sketches" className="w-full h-full object-cover" data-id="element-21" />
            </div>
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -right-6 w-2/3 h-2/3 bg-accent/20 z-0 rounded-sm" data-id="element-22"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 border-t-4 border-l-4 border-accent z-20" data-id="element-23"></div>

            {/* Floating Badge */}
            <div className="absolute bottom-10 -right-10 bg-primary text-white p-6 rounded-sm shadow-xl z-20 hidden md:block" data-id="element-24">
              <div className="text-4xl font-serif font-bold text-accent mb-1" data-id="element-25">
                <Counter end={20} suffix="+" data-id="element-26" />
              </div>
              <div className="text-sm uppercase tracking-wider font-medium" data-id="element-27">
                Years of
                <br data-id="element-28" />
                Mastery
              </div>
            </div>
          </motion.div>

          {/* Text Side */}
          <motion.div initial="hidden" animate={controls} variants={{
          hidden: {
            opacity: 0,
            x: 50
          },
          visible: {
            opacity: 1,
            x: 0,
            transition: {
              duration: 0.8,
              delay: 0.2
            }
          }
        }} data-id="element-29">
            <h2 className="text-accent font-medium tracking-widest uppercase text-sm mb-3" data-id="element-30">
              About Us
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6 leading-tight" data-id="element-31">
              Building Trust, Delivering Quality
            </h3>

            <p className="text-gray-600 text-lg mb-6 leading-relaxed" data-id="element-32">
              With over two decades in the construction and remodeling industry,
              we have perfected the art of transforming spaces. Our approach
              focuses on optimizing every project, ensuring exceptional results
              that exceed expectations.
            </p>

            <p className="text-gray-600 text-lg mb-8 leading-relaxed" data-id="element-33">
              We understand that a remodel can be stressful. That's why we
              handle the entire process: from obtaining permits to daily
              supervision, maintaining transparent communication with our
              clients at all times.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10" data-id="element-34">
              {features.map((feature, index) => <div key={index} className="flex items-start gap-3" data-id="element-35">
                  <CheckCircle2 className="text-accent shrink-0 mt-1" size={20} data-id="element-36" />
                  <span className="text-primary font-medium" data-id="element-37">{feature}</span>
                </div>)}
            </div>

            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-gray-300" data-id="element-38">
              {stats.map((stat, index) => <div key={index} className="text-center sm:text-left" data-id="element-39">
                  <div className="text-accent mb-2 flex justify-center sm:justify-start" data-id="element-40">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-serif font-bold text-primary mb-1" data-id="element-41">
                    <Counter end={stat.value} suffix={stat.suffix} data-id="element-42" />
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-medium" data-id="element-43">
                    {stat.label}
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}